import * as React from "react";
import { cn } from "../lib/utils";

export interface StatPillProps {
  label: string;
  value: string | number;
  /** Tone color — applied to the value. */
  tone?: "default" | "good" | "warn" | "alert" | "info";
  /** Optional trend indicator: "up" | "down" | "flat". */
  trend?: "up" | "down" | "flat";
  /** Optional trend value (e.g., "+12%"). */
  trendValue?: string;
  /** Optional icon to the left of the label. */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

/**
 * A compact key-value pill — used in toolbars, list-page summary rows, and
 * filter bars. Like Odoo's "12 Open / 3 Done / 1 Cancelled" filter chips.
 */
export function StatPill({
  label,
  value,
  tone = "default",
  trend,
  trendValue,
  icon: Icon,
  className,
}: StatPillProps) {
  const toneClass = {
    default: "text-foreground",
    good: "text-[hsl(var(--state-running))]",
    warn: "text-[hsl(var(--alarm-minor))]",
    alert: "text-[hsl(var(--alarm-critical))]",
    info: "text-brand-primary",
  }[tone];

  const trendIcon = {
    up: "▲",
    down: "▼",
    flat: "—",
  };

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5",
        className,
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-sm font-semibold", toneClass)}>{value}</span>
      {trend && trendValue && (
        <span
          className={cn(
            "text-xs",
            trend === "up" && "text-[hsl(var(--state-running))]",
            trend === "down" && "text-[hsl(var(--alarm-critical))]",
            trend === "flat" && "text-muted-foreground",
          )}
        >
          {trendIcon[trend]} {trendValue}
        </span>
      )}
    </div>
  );
}

/** Container that lays a row of StatPills with consistent spacing. */
export function StatPillRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {children}
    </div>
  );
}
